import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAuthSession, bookingApi } from '../services/api';
import DashboardSidebar from '../components/DashboardSidebar';
import ClientBlogsSection from '../components/ClientBlogsSection';
import { buildWhatsAppLink } from '../utils/whatsapp';
import InbuiltWhatsAppModal from '../components/InbuiltWhatsAppModal';
import BookingActionModal from '../components/BookingActionModal';
import NotificationBell from '../components/NotificationBell';

const ClientDashboard = () => {
  const navigate = useNavigate();
  const authSession = getAuthSession();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState('');
  const [whatsappModalOpen, setWhatsappModalOpen] = useState(false);
  const [whatsappTarget, setWhatsappTarget] = useState(null);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [actionModalBooking, setActionModalBooking] = useState(null);
  const [actionModalType, setActionModalType] = useState('reschedule');
  const [sessionFilter, setSessionFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!authSession?.token) {
      navigate('/login');
      return;
    }

    setUserName(authSession.user?.name || 'Friend');

    const fetchBookings = async () => {
      setLoading(true);
      try {
        const response = await bookingApi.mine();
        const raw = response.bookings || response.data || response || [];
        if (Array.isArray(raw)) {
          setBookings(raw);
        }
      } catch (err) {
        console.error('Failed to load bookings:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [authSession?.token, navigate]);

  // Derived stats
  const totalBookings = bookings.length;
  const completedBookings = bookings.filter(b => b.status === 'completed').length;
  const upcomingBookings = bookings.filter(b => b.status === 'confirmed' || b.status === 'pending');
  const nextSession = upcomingBookings[0] || null;
  const progressPercentage = totalBookings > 0 ? Math.round((completedBookings / totalBookings) * 100) : 100;

  // Filtered sessions
  const filteredSessions = upcomingBookings.filter(b => {
    if (sessionFilter === 'online') {
      const mode = b.mode || b.sessionType || 'online';
      if (mode !== 'online') return false;
    }
    if (sessionFilter === 'in-person') {
      const mode = b.mode || b.sessionType || 'online';
      if (mode !== 'in-person') return false;
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const tp = b.physiotherapistProfileId || b.therapistProfile || {};
      const tu = tp.userId || {};
      const therapistName = (tu.name || tp.name || (b.therapist ? `${b.therapist.firstName || ''} ${b.therapist.lastName || ''}` : '')).toLowerCase();
      return therapistName.includes(q);
    }
    return true;
  });

  const formattedDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric'
  });

  return (
    <div className="nexaflow-client-layout">
      <DashboardSidebar userRole="client" />

      <main className="nexaflow-client-main">
        {/* NexaFlow Top Header Bar */}
        <header className="nexaflow-topbar">
          <div className="topbar-left">
            <span className="topbar-breadcrumb">Client Workspace / Overview</span>
            <h1 className="topbar-title">Welcome back, {userName} 👋</h1>
          </div>

          <div className="topbar-right">
            {/* Global Search Bar */}
            <div className="topbar-search">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                placeholder="Search sessions, therapists, resources..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <span className="search-shortcut">⌘K</span>
            </div>

            {/* Date Tag */}
            <div className="topbar-date-chip">
              <span className="chip-dot"></span>
              <span>{formattedDate}</span>
            </div>

            {/* CTA Button */}
            <Link to="/appointment" className="topbar-cta-btn">
              <span>+ Book Session</span>
            </Link>

            {/* Notification Bell */}
            <NotificationBell />
          </div>
        </header>

        <div className="nexaflow-content-container">
          {/* NexaFlow Hero Banner */}
          <div className="nexaflow-welcome-banner">
            <div className="welcome-banner-decor-circle"></div>
            <div className="welcome-banner-decor-pill"></div>
            <div className="welcome-banner-content">
              <div className="welcome-tag">✨ PERSONAL WELLNESS PORTAL</div>
              <h2 className="welcome-heading">Your mental wellness journey, organized & centered.</h2>
              <p className="welcome-description">
                Schedule consultations with verified clinical specialists, complete therapeutic worksheets, and monitor your session progress in real time.
              </p>
              <div className="welcome-actions">
                <Link to="/therapists" className="banner-primary-btn">
                  🔍 Find Therapists
                </Link>
                <Link to="/client/worksheets" className="banner-secondary-btn">
                  📑 View Worksheets
                </Link>
              </div>
            </div>

            {/* Circular Progress Widget on Banner (NexaFlow signature element) */}
            <div className="welcome-progress-card">
              <div className="progress-ring-container">
                <svg className="progress-ring" width="100" height="100">
                  <circle
                    className="progress-ring__circle-bg"
                    stroke="rgba(255, 255, 255, 0.25)"
                    strokeWidth="8"
                    fill="transparent"
                    r="40"
                    cx="50"
                    cy="50"
                  />
                  <circle
                    className="progress-ring__circle"
                    stroke="#ffffff"
                    strokeWidth="8"
                    strokeDasharray={`${2 * Math.PI * 40}`}
                    strokeDashoffset={`${2 * Math.PI * 40 * (1 - (progressPercentage || 100) / 100)}`}
                    strokeLinecap="round"
                    fill="transparent"
                    r="40"
                    cx="50"
                    cy="50"
                  />
                </svg>
                <div className="progress-ring-text">
                  <span className="progress-val">{progressPercentage}%</span>
                  <span className="progress-sub">Track</span>
                </div>
              </div>
              <div className="progress-info">
                <h4>Session Adherence</h4>
                <p>{completedBookings} of {totalBookings || 1} Completed</p>
              </div>
            </div>
          </div>

          {/* NexaFlow 4-Metric KPI Grid */}
          <section className="nexaflow-kpi-grid">
            {/* KPI 1 */}
            <div className="nexaflow-kpi-card">
              <div className="kpi-header">
                <div className="kpi-icon-box purple">
                  <span>📅</span>
                </div>
                <span className="kpi-badge positive">Active</span>
              </div>
              <div className="kpi-content">
                <div className="kpi-number">{upcomingBookings.length}</div>
                <div className="kpi-label">Upcoming Sessions</div>
                <div className="kpi-subtext">
                  {nextSession ? `Next: ${new Date(nextSession.scheduledAt || nextSession.sessionDate || Date.now()).toLocaleDateString()}` : 'Ready for next booking'}
                </div>
              </div>
            </div>

            {/* KPI 2 */}
            <div className="nexaflow-kpi-card">
              <div className="kpi-header">
                <div className="kpi-icon-box lavender">
                  <span>🎯</span>
                </div>
                <span className="kpi-badge neutral">{totalBookings} Total</span>
              </div>
              <div className="kpi-content">
                <div className="kpi-number">{completedBookings}</div>
                <div className="kpi-label">Completed Sessions</div>
                <div className="kpi-subtext">Verified therapist consultations</div>
              </div>
            </div>

            {/* KPI 3 */}
            <div className="nexaflow-kpi-card">
              <div className="kpi-header">
                <div className="kpi-icon-box teal">
                  <span>📑</span>
                </div>
                <span className="kpi-badge positive">Interactive</span>
              </div>
              <div className="kpi-content">
                <div className="kpi-number">Live</div>
                <div className="kpi-label">Clinical Worksheets</div>
                <div className="kpi-subtext">Collaborative therapist forms</div>
              </div>
            </div>

            {/* KPI 4 */}
            <div className="nexaflow-kpi-card">
              <div className="kpi-header">
                <div className="kpi-icon-box emerald">
                  <span>💬</span>
                </div>
                <span className="kpi-badge positive">1-Click</span>
              </div>
              <div className="kpi-content">
                <div className="kpi-number">Inbuilt</div>
                <div className="kpi-label">WhatsApp Messaging</div>
                <div className="kpi-subtext">Instant doctor communication</div>
              </div>
            </div>
          </section>

          {/* NexaFlow Quick Navigation Tiles */}
          <div className="nexaflow-section-title-wrap">
            <h3 className="nexaflow-section-title">Quick Services & Resources</h3>
            <span className="nexaflow-section-subtitle">Jump to essential tools across your care portal</span>
          </div>

          <div className="nexaflow-quick-tiles">
            <Link to="/client/profile" className="nexaflow-tile">
              <div className="tile-icon-box purple">👤</div>
              <div className="tile-text">
                <h4>My Profile</h4>
                <p>Personal goals & medical history</p>
              </div>
              <span className="tile-arrow">→</span>
            </Link>

            <Link to="/client/worksheets" className="nexaflow-tile">
              <div className="tile-icon-box lavender">📑</div>
              <div className="tile-text">
                <h4>My Worksheets</h4>
                <p>Live collaborative exercises</p>
              </div>
              <span className="tile-arrow">→</span>
            </Link>

            <Link to="/client/bookings" className="nexaflow-tile">
              <div className="tile-icon-box blue">📅</div>
              <div className="tile-text">
                <h4>My Bookings</h4>
                <p>Full appointment schedule</p>
              </div>
              <span className="tile-arrow">→</span>
            </Link>

            <Link to="/therapists" className="nexaflow-tile">
              <div className="tile-icon-box pink">🩺</div>
              <div className="tile-text">
                <h4>Find Therapist</h4>
                <p>Browse verified specialists</p>
              </div>
              <span className="tile-arrow">→</span>
            </Link>

            <Link to="/self-help" className="nexaflow-tile">
              <div className="tile-icon-box emerald">🧘</div>
              <div className="tile-text">
                <h4>Self-Help Hub</h4>
                <p>Mindfulness & meditation tools</p>
              </div>
              <span className="tile-arrow">→</span>
            </Link>

            <Link to="/community" className="nexaflow-tile">
              <div className="tile-icon-box amber">👥</div>
              <div className="tile-text">
                <h4>Community Wall</h4>
                <p>Peer discussions & support</p>
              </div>
              <span className="tile-arrow">→</span>
            </Link>
          </div>

          {/* NexaFlow Two-Column Main Section */}
          <div className="nexaflow-two-col-layout">
            {/* Primary Left Column: Upcoming Sessions Pipeline */}
            <div className="nexaflow-main-col">
              <div className="nexaflow-card">
                <div className="card-header-row">
                  <div>
                    <h3 className="card-title">📆 Scheduled Therapy Sessions</h3>
                    <p className="card-subtitle">Manage upcoming consultations, join Google Meet, or chat</p>
                  </div>
                  <Link to="/client/bookings" className="card-link-btn">
                    View All ({bookings.length}) →
                  </Link>
                </div>

                {/* Filter Pill Row */}
                <div className="card-filter-row">
                  <button
                    className={`filter-pill ${sessionFilter === 'all' ? 'active' : ''}`}
                    onClick={() => setSessionFilter('all')}
                  >
                    All Sessions
                  </button>
                  <button
                    className={`filter-pill ${sessionFilter === 'online' ? 'active' : ''}`}
                    onClick={() => setSessionFilter('online')}
                  >
                    💻 Online Calls
                  </button>
                  <button
                    className={`filter-pill ${sessionFilter === 'in-person' ? 'active' : ''}`}
                    onClick={() => setSessionFilter('in-person')}
                  >
                    🏥 In-Person
                  </button>
                </div>

                {/* Sessions List */}
                {loading ? (
                  <div className="nexaflow-loading-state">
                    <div className="nexaflow-spinner"></div>
                    <p>Loading your appointments...</p>
                  </div>
                ) : filteredSessions.length > 0 ? (
                  <div className="nexaflow-sessions-list">
                    {filteredSessions.slice(0, 5).map((booking) => {
                      const scheduledDate = new Date(booking.scheduledAt || booking.sessionDate || booking.date || Date.now());
                      const tp = booking.physiotherapistProfileId || booking.therapistProfile || {};
                      const tu = tp.userId || {};
                      const therapistName = tu.name || tp.name || (booking.therapist ? `Dr. ${booking.therapist.firstName || ''} ${booking.therapist.lastName || ''}`.trim() : 'Therapist');
                      const therapistPhone = tu.phone || tp.phone || booking.therapistPhone || '';
                      const therapistEmail = tu.email || tp.email || booking.therapistEmail || '';
                      const therapistUserId = tu._id || tu.id || tp._id || tp.id || booking.physiotherapistProfileId;
                      const sessionMode = booking.mode || booking.sessionType || 'online';
                      const timeString = scheduledDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

                      return (
                        <div key={booking._id} className="nexaflow-session-item">
                          {/* Date Block */}
                          <div className="session-date-pill">
                            <span className="session-day">{scheduledDate.getDate()}</span>
                            <span className="session-month">{scheduledDate.toLocaleString('default', { month: 'short' })}</span>
                          </div>

                          {/* Info */}
                          <div className="session-details">
                            <div className="session-title-wrap">
                              <h4 className="session-therapist-name">{therapistName}</h4>
                              <span className={`session-mode-badge ${sessionMode}`}>
                                {sessionMode === 'online' ? '💻 Online Meet' : '🏥 Clinic Visit'}
                              </span>
                            </div>
                            <div className="session-meta">
                              <span className="meta-item">🕐 {timeString}</span>
                              <span className="meta-dot">•</span>
                              <span className={`meta-status ${booking.status}`}>{booking.status}</span>
                            </div>
                            {booking.rescheduleReason && (
                              <div className="reschedule-note">
                                🔄 Reschedule note: {booking.rescheduleReason}
                              </div>
                            )}
                          </div>

                          {/* Action Buttons */}
                          <div className="session-action-buttons">
                            {sessionMode === 'online' && (booking.googleMeetLink || booking.meetingLink) && (
                              <a
                                href={booking.googleMeetLink || booking.meetingLink}
                                className="action-btn-meet"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                🎥 Join Meet
                              </a>
                            )}

                            <button
                              onClick={() => {
                                setWhatsappTarget({
                                  _id: therapistUserId,
                                  name: therapistName,
                                  phone: therapistPhone,
                                  email: therapistEmail,
                                  role: 'therapist'
                                });
                                setWhatsappModalOpen(true);
                              }}
                              className="action-btn-whatsapp-inbuilt"
                              title="Open Inbuilt WhatsApp Chat"
                            >
                              💬 Inbuilt Chat
                            </button>

                            {therapistPhone && (
                              <a
                                href={buildWhatsAppLink(therapistPhone, `Hello ${therapistName}, I am reaching out regarding my therapy session on PsychMitra.`)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="action-btn-whatsapp-app"
                                title="Open in Official WhatsApp App"
                              >
                                📱 App
                              </a>
                            )}

                            {booking.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => {
                                    setActionModalBooking(booking);
                                    setActionModalType('reschedule');
                                    setActionModalOpen(true);
                                  }}
                                  className="action-btn-reschedule"
                                  title="Reschedule Appointment"
                                >
                                  🔄
                                </button>

                                <button
                                  onClick={() => {
                                    setActionModalBooking(booking);
                                    setActionModalType('cancel');
                                    setActionModalOpen(true);
                                  }}
                                  className="action-btn-cancel"
                                  title="Cancel Appointment"
                                >
                                  ✕
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="nexaflow-empty-card">
                    <div className="empty-icon-ring">📅</div>
                    <h4>No upcoming sessions scheduled</h4>
                    <p>Connect with a verified mental health practitioner to start your consultation journey.</p>
                    <Link to="/therapists" className="empty-action-btn">
                      Find a Therapist
                    </Link>
                  </div>
                )}
              </div>
            </div>

            {/* Secondary Right Column: Widgets */}
            <div className="nexaflow-side-col">
              {/* Quick WhatsApp Connect Widget */}
              <div className="nexaflow-card sidebar-widget">
                <div className="widget-header">
                  <span className="widget-icon-pill green">💬</span>
                  <div>
                    <h4 className="widget-title">Direct Doctor Chat</h4>
                    <p className="widget-subtitle">Seamless Inbuilt WhatsApp messaging</p>
                  </div>
                </div>
                <p className="widget-body-text">
                  Communicate securely with your assigned practitioners, send updates, and view session notes without leaving your portal.
                </p>
                <Link to="/client/bookings" className="widget-action-link">
                  Open Chat Directory →
                </Link>
              </div>

              {/* Clinical Worksheets Highlight */}
              <div className="nexaflow-card sidebar-widget">
                <div className="widget-header">
                  <span className="widget-icon-pill purple">📑</span>
                  <div>
                    <h4 className="widget-title">Clinical Exercises</h4>
                    <p className="widget-subtitle">Live interactive worksheets</p>
                  </div>
                </div>
                <div className="widget-items-list">
                  <div className="widget-item">
                    <span className="item-icon">✓</span>
                    <div className="item-text">
                      <strong>CBT Thought Records</strong>
                      <small>Cognitive Restructuring</small>
                    </div>
                  </div>
                  <div className="widget-item">
                    <span className="item-icon">✓</span>
                    <div className="item-text">
                      <strong>Anxiety & Worry Log</strong>
                      <small>Trigger & Response Tracking</small>
                    </div>
                  </div>
                  <div className="widget-item">
                    <span className="item-icon">✓</span>
                    <div className="item-text">
                      <strong>Sleep & Habit Journal</strong>
                      <small>Daily Mindfulness</small>
                    </div>
                  </div>
                </div>
                <Link to="/client/worksheets" className="widget-action-link">
                  Access My Worksheets →
                </Link>
              </div>

              {/* Self-Help Tools Widget */}
              <div className="nexaflow-card sidebar-widget">
                <div className="widget-header">
                  <span className="widget-icon-pill amber">🧘</span>
                  <div>
                    <h4 className="widget-title">Self-Help & Resources</h4>
                    <p className="widget-subtitle">24/7 guided recovery materials</p>
                  </div>
                </div>
                <p className="widget-body-text">
                  Listen to calming meditations, access certified e-books, and read insights curated by clinical psychologists.
                </p>
                <div className="widget-quick-links">
                  <Link to="/self-help" className="pill-link">🧘 Meditation</Link>
                  <Link to="/products" className="pill-link">📚 Store</Link>
                  <Link to="/events" className="pill-link">🎉 Events</Link>
                </div>
              </div>
            </div>
          </div>

          {/* Blogs Section */}
          <div className="nexaflow-blogs-container">
            <div className="nexaflow-section-title-wrap">
              <h3 className="nexaflow-section-title">Clinical Insights & Well-being Articles</h3>
              <span className="nexaflow-section-subtitle">Authored by certified mental health practitioners</span>
            </div>
            <ClientBlogsSection />
          </div>
        </div>

        {/* Inbuilt WhatsApp Modal */}
        <InbuiltWhatsAppModal
          isOpen={whatsappModalOpen}
          onClose={() => setWhatsappModalOpen(false)}
          targetUser={whatsappTarget}
          contactsList={bookings.map(b => {
            const tp = b.physiotherapistProfileId || b.therapistProfile || {};
            const tu = tp.userId || {};
            return {
              _id: tu._id || tu.id || tp._id || tp.id || b.physiotherapistProfileId,
              name: tu.name || tp.name || (b.therapist ? `Dr. ${b.therapist.firstName || ''} ${b.therapist.lastName || ''}`.trim() : 'Therapist'),
              phone: tu.phone || tp.phone || b.therapistPhone || '',
              email: tu.email || tp.email || b.therapistEmail || '',
              role: 'therapist',
              status: b.status,
              isSessionCompleted: b.status === 'completed'
            };
          })}
        />

        {/* Booking Action Modal */}
        <BookingActionModal
          isOpen={actionModalOpen}
          onClose={() => setActionModalOpen(false)}
          booking={actionModalBooking}
          actionType={actionModalType}
          userRole="client"
          onSuccess={async () => {
            try {
              const res = await bookingApi.mine();
              const raw = res.bookings || res.data || res || [];
              if (Array.isArray(raw)) {
                setBookings(raw);
              }
            } catch (e) {
              console.error(e);
            }
          }}
        />
      </main>

      <style>{`
        /* =========================================================
           NEXAFLOW CLIENT PANEL STYLES
           Design Language Inspired by NexaFlow Behance Dashboard
           ========================================================= */

        .nexaflow-client-layout {
          display: flex;
          min-height: 100vh;
          background: #F8F9FD;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          color: #1A162B;
        }

        .nexaflow-client-main {
          flex: 1;
          margin-left: 270px;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          background: #F8F9FD;
        }

        /* Topbar Header */
        .nexaflow-topbar {
          background: #FFFFFF;
          border-bottom: 1px solid rgba(122, 96, 202, 0.1);
          padding: 16px 36px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          position: sticky;
          top: 0;
          z-index: 100;
          box-shadow: 0 2px 12px rgba(122, 96, 202, 0.03);
          flex-wrap: wrap;
          gap: 16px;
        }

        .topbar-left {
          display: flex;
          flex-direction: column;
        }

        .topbar-breadcrumb {
          font-size: 11.5px;
          font-weight: 700;
          color: #7A60CA;
          text-transform: uppercase;
          letter-spacing: 0.6px;
        }

        .topbar-title {
          margin: 2px 0 0 0;
          font-size: 20px;
          font-weight: 800;
          color: #1A162B;
          letter-spacing: -0.4px;
        }

        .topbar-right {
          display: flex;
          align-items: center;
          gap: 14px;
          flex-wrap: wrap;
        }

        .topbar-search {
          display: flex;
          align-items: center;
          gap: 8px;
          background: #F6F4FD;
          border: 1px solid rgba(122, 96, 202, 0.15);
          border-radius: 12px;
          padding: 8px 14px;
          min-width: 280px;
          transition: all 0.2s ease;
        }

        .topbar-search:focus-within {
          background: #FFFFFF;
          border-color: #7A60CA;
          box-shadow: 0 0 0 3px rgba(122, 96, 202, 0.15);
        }

        .topbar-search input {
          border: none;
          background: transparent;
          font-size: 13.5px;
          outline: none;
          color: #1A162B;
          width: 100%;
        }

        .search-icon {
          font-size: 14px;
          opacity: 0.6;
        }

        .search-shortcut {
          font-size: 10.5px;
          background: #EDE9FE;
          color: #7A60CA;
          padding: 2px 6px;
          border-radius: 5px;
          font-weight: 700;
        }

        .topbar-date-chip {
          display: flex;
          align-items: center;
          gap: 8px;
          background: #FFFFFF;
          border: 1px solid rgba(122, 96, 202, 0.12);
          padding: 8px 14px;
          border-radius: 12px;
          font-size: 13px;
          font-weight: 600;
          color: #64748B;
        }

        .chip-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #10B981;
          box-shadow: 0 0 8px rgba(16, 185, 129, 0.5);
        }

        .topbar-cta-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: #7A60CA;
          color: #ffffff;
          padding: 9px 18px;
          border-radius: 12px;
          font-size: 13.5px;
          font-weight: 700;
          text-decoration: none;
          transition: all 0.2s ease;
          box-shadow: 0 4px 14px rgba(122, 96, 202, 0.3);
        }

        .topbar-cta-btn:hover {
          background: #6747C7;
          transform: translateY(-1px);
          box-shadow: 0 6px 18px rgba(122, 96, 202, 0.4);
        }

        /* Container */
        .nexaflow-content-container {
          padding: 30px 36px 60px 36px;
          max-width: 1440px;
          width: 100%;
          box-sizing: border-box;
          margin: 0 auto;
        }

        .nexaflow-mascot-wrapper {
          margin-bottom: 20px;
        }

        /* NexaFlow Hero Banner */
        .nexaflow-welcome-banner {
          background: linear-gradient(135deg, #7A60CA 0%, #5E3EB5 100%);
          border-radius: 20px;
          padding: 32px 36px;
          color: #ffffff;
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 28px;
          position: relative;
          overflow: hidden;
          box-shadow: 0 10px 30px -5px rgba(122, 96, 202, 0.35);
          gap: 24px;
          flex-wrap: wrap;
        }

        .welcome-banner-decor-circle {
          position: absolute;
          top: -60px;
          right: 200px;
          width: 220px;
          height: 220px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.08);
          pointer-events: none;
        }

        .welcome-banner-decor-pill {
          position: absolute;
          bottom: -40px;
          left: 30%;
          width: 180px;
          height: 90px;
          border-radius: 50px;
          background: rgba(255, 255, 255, 0.05);
          pointer-events: none;
        }

        .welcome-banner-content {
          max-width: 620px;
          position: relative;
          z-index: 1;
        }

        .welcome-tag {
          display: inline-block;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 1px;
          background: rgba(255, 255, 255, 0.2);
          padding: 4px 12px;
          border-radius: 20px;
          margin-bottom: 12px;
          backdrop-filter: blur(10px);
        }

        .welcome-heading {
          margin: 0 0 10px 0;
          font-size: 26px;
          font-weight: 800;
          line-height: 1.25;
          letter-spacing: -0.5px;
        }

        .welcome-description {
          margin: 0 0 20px 0;
          font-size: 14.5px;
          opacity: 0.92;
          line-height: 1.55;
        }

        .welcome-actions {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .banner-primary-btn {
          padding: 10px 22px;
          background: #ffffff;
          color: #7A60CA;
          border-radius: 12px;
          font-weight: 700;
          font-size: 13.5px;
          text-decoration: none;
          box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
          transition: all 0.2s ease;
        }
        .banner-primary-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
        }

        .banner-secondary-btn {
          padding: 10px 20px;
          background: rgba(255, 255, 255, 0.2);
          color: #ffffff;
          border: 1px solid rgba(255, 255, 255, 0.35);
          border-radius: 12px;
          font-weight: 700;
          font-size: 13.5px;
          text-decoration: none;
          backdrop-filter: blur(8px);
          transition: all 0.2s ease;
        }
        .banner-secondary-btn:hover {
          background: rgba(255, 255, 255, 0.3);
        }

        /* Progress Card on Banner */
        .welcome-progress-card {
          background: rgba(255, 255, 255, 0.15);
          border: 1px solid rgba(255, 255, 255, 0.25);
          backdrop-filter: blur(16px);
          border-radius: 18px;
          padding: 20px 26px;
          display: flex;
          align-items: center;
          gap: 20px;
          position: relative;
          z-index: 1;
        }

        .progress-ring-container {
          position: relative;
          width: 100px;
          height: 100px;
        }

        .progress-ring {
          transform: rotate(-90deg);
        }

        .progress-ring__circle {
          transition: stroke-dashoffset 0.5s ease-in-out;
        }

        .progress-ring-text {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }

        .progress-val {
          font-size: 20px;
          font-weight: 900;
          line-height: 1;
        }

        .progress-sub {
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          opacity: 0.8;
          margin-top: 2px;
        }

        .progress-info h4 {
          margin: 0 0 4px 0;
          font-size: 16px;
          font-weight: 800;
        }

        .progress-info p {
          margin: 0;
          font-size: 13px;
          opacity: 0.85;
        }

        /* 4-KPI Grid (NexaFlow Style) */
        .nexaflow-kpi-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
          gap: 18px;
          margin-bottom: 28px;
        }

        .nexaflow-kpi-card {
          background: #ffffff;
          border-radius: 16px;
          padding: 22px;
          border: 1px solid rgba(122, 96, 202, 0.1);
          box-shadow: 0 4px 18px rgba(122, 96, 202, 0.04);
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        .nexaflow-kpi-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 24px rgba(122, 96, 202, 0.08);
          border-color: rgba(122, 96, 202, 0.25);
        }

        .kpi-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 14px;
        }

        .kpi-icon-box {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
        }

        .kpi-icon-box.purple { background: #EDE9FE; color: #7A60CA; }
        .kpi-icon-box.lavender { background: #F3E8FF; color: #9333EA; }
        .kpi-icon-box.teal { background: #CCFBF1; color: #0F766E; }
        .kpi-icon-box.emerald { background: #D1FAE5; color: #047857; }

        .kpi-badge {
          font-size: 11px;
          font-weight: 700;
          padding: 3px 9px;
          border-radius: 20px;
          text-transform: uppercase;
          letter-spacing: 0.4px;
        }

        .kpi-badge.positive { background: #ECFDF5; color: #059669; }
        .kpi-badge.neutral { background: #F1F5F9; color: #475569; }

        .kpi-number {
          font-size: 26px;
          font-weight: 900;
          color: #1A162B;
          letter-spacing: -0.5px;
          line-height: 1.1;
        }

        .kpi-label {
          font-size: 13.5px;
          font-weight: 700;
          color: #475569;
          margin: 4px 0 2px 0;
        }

        .kpi-subtext {
          font-size: 12px;
          color: #94A3B8;
        }

        /* Section Titles */
        .nexaflow-section-title-wrap {
          margin-bottom: 16px;
        }

        .nexaflow-section-title {
          margin: 0;
          font-size: 18px;
          font-weight: 800;
          color: #1A162B;
          letter-spacing: -0.3px;
        }

        .nexaflow-section-subtitle {
          font-size: 12.5px;
          color: #64748B;
        }

        /* Quick Tiles */
        .nexaflow-quick-tiles {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 14px;
          margin-bottom: 30px;
        }

        .nexaflow-tile {
          background: #ffffff;
          border-radius: 14px;
          padding: 16px;
          border: 1px solid rgba(122, 96, 202, 0.08);
          box-shadow: 0 2px 10px rgba(122, 96, 202, 0.03);
          display: flex;
          align-items: center;
          gap: 12px;
          text-decoration: none;
          transition: all 0.2s ease;
          position: relative;
        }

        .nexaflow-tile:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 18px rgba(122, 96, 202, 0.09);
          border-color: rgba(122, 96, 202, 0.25);
        }

        .tile-icon-box {
          width: 42px;
          height: 42px;
          border-radius: 11px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          flex-shrink: 0;
        }

        .tile-icon-box.purple { background: #EDE9FE; color: #7A60CA; }
        .tile-icon-box.lavender { background: #F3E8FF; color: #9333EA; }
        .tile-icon-box.blue { background: #E0F2FE; color: #0284C7; }
        .tile-icon-box.pink { background: #FCE7F3; color: #DB2777; }
        .tile-icon-box.emerald { background: #D1FAE5; color: #059669; }
        .tile-icon-box.amber { background: #FEF3C7; color: #D97706; }

        .tile-text h4 {
          margin: 0;
          font-size: 14px;
          font-weight: 700;
          color: #1A162B;
        }

        .tile-text p {
          margin: 2px 0 0 0;
          font-size: 11.5px;
          color: #64748B;
        }

        .tile-arrow {
          margin-left: auto;
          color: #CBD5E1;
          font-weight: 700;
          font-size: 15px;
          transition: transform 0.2s ease;
        }

        .nexaflow-tile:hover .tile-arrow {
          color: #7A60CA;
          transform: translateX(3px);
        }

        /* Two Column Layout */
        .nexaflow-two-col-layout {
          display: grid;
          grid-template-columns: 1.8fr 1fr;
          gap: 24px;
          margin-bottom: 36px;
        }

        .nexaflow-card {
          background: #ffffff;
          border-radius: 18px;
          padding: 26px;
          border: 1px solid rgba(122, 96, 202, 0.1);
          box-shadow: 0 4px 20px rgba(122, 96, 202, 0.04);
        }

        .card-header-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 18px;
          gap: 12px;
          flex-wrap: wrap;
        }

        .card-title {
          margin: 0;
          font-size: 18px;
          font-weight: 800;
          color: #1A162B;
        }

        .card-subtitle {
          margin: 4px 0 0 0;
          font-size: 12.5px;
          color: #64748B;
        }

        .card-link-btn {
          font-size: 13px;
          font-weight: 700;
          color: #7A60CA;
          text-decoration: none;
          transition: color 0.2s ease;
        }
        .card-link-btn:hover {
          color: #5E3EB5;
        }

        .card-filter-row {
          display: flex;
          gap: 8px;
          margin-bottom: 20px;
          border-bottom: 1px solid #F1F5F9;
          padding-bottom: 14px;
        }

        .filter-pill {
          padding: 6px 14px;
          border-radius: 20px;
          border: 1px solid #E2E8F0;
          background: #F8FAFC;
          color: #64748B;
          font-size: 12.5px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .filter-pill.active {
          background: #7A60CA;
          color: #ffffff;
          border-color: #7A60CA;
          box-shadow: 0 2px 8px rgba(122, 96, 202, 0.25);
        }

        /* Sessions List */
        .nexaflow-sessions-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .nexaflow-session-item {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px 18px;
          background: #FAF9FE;
          border-radius: 14px;
          border: 1px solid rgba(122, 96, 202, 0.08);
          transition: all 0.2s ease;
        }

        .nexaflow-session-item:hover {
          background: #FFFFFF;
          border-color: rgba(122, 96, 202, 0.2);
          box-shadow: 0 4px 14px rgba(122, 96, 202, 0.06);
          transform: translateY(-1px);
        }

        .session-date-pill {
          width: 52px;
          height: 52px;
          background: linear-gradient(135deg, #7A60CA 0%, #6347BF 100%);
          border-radius: 12px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: #ffffff;
          flex-shrink: 0;
          box-shadow: 0 4px 10px rgba(122, 96, 202, 0.25);
        }

        .session-day {
          font-size: 20px;
          font-weight: 900;
          line-height: 1;
        }

        .session-month {
          font-size: 10.5px;
          font-weight: 700;
          text-transform: uppercase;
          margin-top: 2px;
          opacity: 0.9;
        }

        .session-details {
          flex: 1;
          min-width: 0;
        }

        .session-title-wrap {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }

        .session-therapist-name {
          margin: 0;
          font-size: 15px;
          font-weight: 800;
          color: #1A162B;
        }

        .session-mode-badge {
          font-size: 11px;
          font-weight: 700;
          padding: 2px 8px;
          border-radius: 12px;
        }
        .session-mode-badge.online { background: #E0F2FE; color: #0284C7; }
        .session-mode-badge.in-person { background: #D1FAE5; color: #059669; }

        .session-meta {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 4px;
          font-size: 12.5px;
          color: #64748B;
        }

        .meta-status {
          font-weight: 700;
          text-transform: capitalize;
        }
        .meta-status.confirmed { color: #059669; }
        .meta-status.pending { color: #D97706; }

        .reschedule-note {
          font-size: 11.5px;
          color: #7A60CA;
          background: #EDE9FE;
          padding: 3px 8px;
          border-radius: 6px;
          margin-top: 6px;
          display: inline-block;
        }

        .session-action-buttons {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }

        .action-btn-meet {
          background: #2563EB;
          color: #ffffff;
          padding: 7px 14px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 700;
          text-decoration: none;
          box-shadow: 0 2px 6px rgba(37, 99, 235, 0.25);
          transition: background 0.2s ease;
        }
        .action-btn-meet:hover { background: #1D4ED8; }

        .action-btn-whatsapp-inbuilt {
          background: #00A884;
          color: #ffffff;
          border: none;
          padding: 7px 14px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(0, 168, 132, 0.25);
          transition: background 0.2s ease;
        }
        .action-btn-whatsapp-inbuilt:hover { background: #008F6F; }

        .action-btn-whatsapp-app {
          background: #25D366;
          color: #ffffff;
          padding: 7px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 700;
          text-decoration: none;
          box-shadow: 0 2px 6px rgba(37, 211, 102, 0.25);
        }

        .action-btn-reschedule {
          background: #EDE9FE;
          color: #7A60CA;
          border: 1px solid #DDD6FE;
          border-radius: 50%;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 13px;
          cursor: pointer;
        }

        .action-btn-cancel {
          background: #FFF1F2;
          color: #E11D48;
          border: 1px solid #FECDD3;
          border-radius: 50%;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 13px;
          cursor: pointer;
        }

        /* Empty Card */
        .nexaflow-empty-card {
          text-align: center;
          padding: 36px 20px;
          background: #FAF9FE;
          border-radius: 14px;
          border: 1px dashed rgba(122, 96, 202, 0.2);
        }

        .empty-icon-ring {
          font-size: 36px;
          margin-bottom: 10px;
        }

        .nexaflow-empty-card h4 {
          margin: 0 0 6px 0;
          font-size: 16px;
          font-weight: 800;
          color: #1A162B;
        }

        .nexaflow-empty-card p {
          margin: 0 0 16px 0;
          font-size: 13px;
          color: #64748B;
          max-width: 380px;
          margin-left: auto;
          margin-right: auto;
        }

        .empty-action-btn {
          display: inline-block;
          background: #7A60CA;
          color: #ffffff;
          padding: 9px 20px;
          border-radius: 12px;
          font-size: 13px;
          font-weight: 700;
          text-decoration: none;
          box-shadow: 0 4px 12px rgba(122, 96, 202, 0.25);
        }

        /* Sidebar Widgets (Right Col) */
        .nexaflow-side-col {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .sidebar-widget {
          padding: 22px;
        }

        .widget-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 12px;
        }

        .widget-icon-pill {
          width: 38px;
          height: 38px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          flex-shrink: 0;
        }
        .widget-icon-pill.green { background: #D1FAE5; color: #059669; }
        .widget-icon-pill.purple { background: #EDE9FE; color: #7A60CA; }
        .widget-icon-pill.amber { background: #FEF3C7; color: #D97706; }

        .widget-title {
          margin: 0;
          font-size: 15px;
          font-weight: 800;
          color: #1A162B;
        }

        .widget-subtitle {
          margin: 2px 0 0 0;
          font-size: 11.5px;
          color: #64748B;
        }

        .widget-body-text {
          margin: 0 0 14px 0;
          font-size: 13px;
          color: #64748B;
          line-height: 1.5;
        }

        .widget-action-link {
          font-size: 13px;
          font-weight: 700;
          color: #7A60CA;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }
        .widget-action-link:hover {
          color: #5E3EB5;
          text-decoration: underline;
        }

        .widget-items-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-bottom: 14px;
        }

        .widget-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 12px;
          background: #F8FAFC;
          border-radius: 10px;
          border: 1px solid #E2E8F0;
        }

        .item-icon {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #EDE9FE;
          color: #7A60CA;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 800;
        }

        .item-text strong {
          display: block;
          font-size: 12.5px;
          color: #1A162B;
        }

        .item-text small {
          font-size: 11px;
          color: #64748B;
        }

        .widget-quick-links {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .pill-link {
          padding: 6px 12px;
          background: #F1F5F9;
          border-radius: 20px;
          color: #475569;
          font-size: 12px;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.2s ease;
        }
        .pill-link:hover {
          background: #EDE9FE;
          color: #7A60CA;
        }

        /* Loading */
        .nexaflow-loading-state {
          text-align: center;
          padding: 40px;
          color: #64748B;
        }

        .nexaflow-spinner {
          width: 32px;
          height: 32px;
          border: 3px solid rgba(122, 96, 202, 0.2);
          border-top-color: #7A60CA;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin: 0 auto 12px auto;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Responsive Breakpoints */
        @media (max-width: 1024px) {
          .nexaflow-two-col-layout {
            grid-template-columns: 1fr;
          }
          .nexaflow-client-dashboard {
            overflow-x: hidden;
            width: 100%;
          }
          .nexaflow-client-main {
            margin-left: 0;
            width: 100%;
            overflow-x: hidden;
          }
          .nexaflow-topbar {
            padding: 12px 16px 12px 64px;
            gap: 10px;
          }
          .topbar-left {
            flex: 1;
            min-width: 0;
          }
          .topbar-breadcrumb {
            display: none;
          }
          .topbar-title {
            font-size: 16px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .topbar-right {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-left: auto;
          }
          .topbar-date-chip {
            display: none;
          }
          .topbar-search {
            order: 3;
            width: 100%;
            min-width: 100%;
            box-sizing: border-box;
          }
          .topbar-cta-btn {
            padding: 8px 12px;
            font-size: 12px;
            border-radius: 10px;
          }
          .nexaflow-content-container {
            padding: 18px 16px 40px 16px;
            width: 100%;
            box-sizing: border-box;
          }
          .nexaflow-welcome-banner {
            flex-direction: column;
            align-items: flex-start;
            padding: 22px 18px;
            gap: 18px;
          }
          .welcome-heading {
            font-size: 21px;
            line-height: 1.3;
          }
          .welcome-adherence-card {
            width: 100%;
            min-width: 0;
            box-sizing: border-box;
          }
        }

        @media (max-width: 600px) {
          .nexaflow-kpi-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 12px;
          }
          .quick-tools-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 12px;
          }
          .nexaflow-welcome-banner {
            padding: 20px 16px;
          }
          .welcome-heading {
            font-size: 19px;
          }
          .nexaflow-session-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }
          .session-action-buttons {
            width: 100%;
            flex-wrap: wrap;
            justify-content: flex-start;
          }
        }

        @media (max-width: 380px) {
          .nexaflow-kpi-grid,
          .quick-tools-grid {
            grid-template-columns: 1fr !important;
          }
          .nexaflow-topbar {
            padding-left: 56px;
          }
        }
      `}</style>
    </div>
  );
};

export default ClientDashboard;
